'use client';

import cx from 'classnames';
import { UserCrumb } from '@/components/UserCrumb';
import type { User } from '@/lib/types';
import { makeUserUrl } from '@/lib/url';
import Link from 'next/link';
import { ReactNode, useMemo, useState } from 'react';
import styles from './UserTable.module.css';

export const UserTable = <TUserRow extends { user: User }>({
  rows,
  schema,
  sortBy: initialSortBy,
  sortDirection: initialSortDirection,
}: {
  rows: TUserRow[];
  schema: {
    key: keyof TUserRow;
    label: string;
    className?: string;
  }[];
  sortBy?: keyof TUserRow;
  sortDirection?: 'asc' | 'desc';
}) => {
  const [sortBy, setSortBy] = useState<keyof TUserRow | null>(initialSortBy ?? null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialSortDirection ?? 'desc');

  const sortedRows = useMemo(
    () => sortBy ? rows.toSorted((a, b) => {
      if (sortDirection === 'asc') {
        return (a[sortBy] as number) - (b[sortBy] as number);
      }

      return (b[sortBy] as number) - (a[sortBy] as number);
    }) : rows,
    [sortBy, sortDirection, rows],
  );

  const handleSort = (key: keyof TUserRow) => {
    if (sortBy === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
    }
  };

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>
            <span>User</span>
          </th>
          {schema.map(({ label, key }) => (
            <th className={cx(key === sortBy && styles.active)} key={label} onClick={() => handleSort(key)}>
              <span>
                {label}
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map(row => (
          <tr key={row.user.name}>
            <td>
              <Link href={makeUserUrl(row.user.name)}>
                <UserCrumb
                  user={row.user}
                  className={styles.userCrumb}
                />
              </Link>
            </td>
            {schema.map(({ key, className }) => (
              <td key={`${row.user.name}-${key as string}`} className={className}>
                {row[key] as ReactNode}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
